/* Playo (Linear & FAST channels) API calls. Everything goes through
   axiosInstance via api/methods (Bearer token + tenant headers). The backend
   wraps responses in { status, message, data }. Admin API: /api/v1/playo
   (Backend/docs/PLAYO.md), channel-ready: /api/v1/playo/assets
   (PLAYO_CHANNEL_READY.md), live HLS: /playo/... (PLAYO_MANIFEST.md). */

import { get, post, put, del } from "../../api/methods";
import { getApiBaseUrl } from "../../api/tenantApi";
import { chunk, toIso } from "./playoTime";

const unwrap = (res) => res?.data?.data;

/** Error text from an API error: first field error, else message. */
export function apiErrorMessage(err, fallback = "Something went wrong") {
  const data = err?.response?.data;
  const errors = data?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).find((v) => typeof v === "string" && v);
    if (first) return first;
  }
  return data?.message || err?.message || fallback;
}

export const listChannels = async ({ q = "", status = "" } = {}) => {
  const qs = new URLSearchParams();
  if (q) qs.set("q", q);
  if (status) qs.set("status", status);
  const res = await get(`/playo/channels${qs.toString() ? `?${qs}` : ""}`);
  const data = unwrap(res);
  return Array.isArray(data) ? data : [];
};

export const getChannel = async (id) => unwrap(await get(`/playo/channels/${id}`));

/** Returns { channel, message } (message may carry a channel-ready warning). */
export const saveChannel = async (id, body) => {
  const res = id ? await put(`/playo/channels/${id}`, body) : await post("/playo/channels", body);
  return { channel: unwrap(res), message: res?.data?.message || "" };
};

export const deleteChannel = async (id) => del(`/playo/channels/${id}`);

/**
 * Check an external .m3u8 (reachable, HLS, live). Resolves to
 * { ok, status: 'online'|'offline', kind, live, variants, error }.
 * With channelId (and the same URL as saved) the backend also stores the result.
 */
export const testSource = async (url, channelId = null) =>
  unwrap(await post("/playo/channels/test-source", { url, ...(channelId ? { channel_id: channelId } : {}) }));

/** Items overlapping [from, to) (ms). Max 31 days per call. */
export const getSchedule = async (channelId, from, to) => {
  const res = await get(`/playo/channels/${channelId}/schedule?from=${encodeURIComponent(toIso(from))}&to=${encodeURIComponent(toIso(to))}`);
  const data = unwrap(res);
  return Array.isArray(data?.items) ? data.items : [];
};

export const createItem = async (channelId, body) => {
  const res = await post(`/playo/channels/${channelId}/schedule/items`, body);
  return { item: unwrap(res), message: res?.data?.message || "" };
};

export const updateItem = async (channelId, itemId, body) => {
  const res = await put(`/playo/channels/${channelId}/schedule/items/${itemId}`, body);
  return { item: unwrap(res), message: res?.data?.message || "" };
};

export const deleteItem = async (channelId, itemId) => del(`/playo/channels/${channelId}/schedule/items/${itemId}`);

/**
 * Bulk create in chunks of ≤ 500 (each chunk is all-or-nothing). Returns { created, replaced, notReady }.
 * `replace: { from, to }` (ms) also deletes the items starting in [from, to), in
 * the same transaction as the insert, so it must fit in one chunk. With
 * `replace` and no items it just clears that range.
 */
export const bulkCreate = async (channelId, items, { replace = null } = {}) => {
  let created = 0;
  let replaced = 0;
  const notReady = new Set();
  const parts = items.length ? chunk(items) : replace ? [[]] : [];
  if (replace && parts.length > 1) throw new Error(`Can't replace a day with more than ${parts[0].length} items at once`);
  for (const part of parts) {
    const body = replace ? { items: part, replace: { from: toIso(replace.from), to: toIso(replace.to) } } : { items: part };
    const res = await post(`/playo/channels/${channelId}/schedule/items/bulk`, body);
    const data = unwrap(res);
    created += Number(data?.created) || (Array.isArray(data?.items) ? data.items.length : part.length);
    replaced += Number(data?.replaced) || 0;
    (data?.not_channel_ready || []).forEach((x) => notReady.add(x?.asset_id ?? x));
  }
  return { created, replaced, notReady: [...notReady] };
};

export const getChannelReady = async (videoId) => unwrap(await get(`/playo/assets/${videoId}/channel-ready`));
/** The tenant's transcodes in flight, in queue order: { items: [{ video_id, title, state, position, progress, next_use }] }. */
export const getReadyQueue = async () => unwrap(await get(`/playo/assets/channel-ready/queue`));
export const makeChannelReady = async (videoId) => unwrap(await post(`/playo/assets/${videoId}/channel-ready`, {}));

/**
 * Expand a Media Library selection into the ordered videos to schedule.
 * `picks` are the Media Library's content items (movies, clips, whole series)
 * and/or raw Video rows; a series comes back as its published episodes in
 * watch order. Returns { items: [{ video_id, name, duration_sec, image, ... }], skipped }.
 */
export const resolveSelection = async (picks) => {
  const items = (picks || [])
    .map((p) => {
      if (!p) return null;
      if (p.media_type === "video" && !p.content_type) return { type: "video", id: p.id };
      const isSeries = String(p.content_type || "").toLowerCase() === "series";
      const id = p.content_id ?? (isSeries ? Math.abs(p.id) : p.id);
      return { type: isSeries ? "series" : "videoMeta", id };
    })
    .filter((x) => x && Number(x.id) > 0);
  if (!items.length) return { items: [], skipped: [] };
  const data = unwrap(await post("/playo/assets/resolve", { items }));
  return { items: data?.items || [], skipped: data?.skipped || [] };
};

/** Media-library search: content rows that have a playable video ({ video_id, name, ... }). */
export const searchAssets = async (search = "", page = 1) => {
  const res = await get(`/all-content?page=${page}&type=all&search=${encodeURIComponent(search)}&limit=20`);
  const data = unwrap(res);
  const rows = Array.isArray(data?.contents) ? data.contents : [];
  return rows
    .filter((r) => r && r.video_id)
    .map((r) => ({
      video_id: r.video_id,
      name: r.name || r.title || `Video ${r.video_id}`,
      image: r.landscape || r.portrait || null,
      type: r.library_type || r.content_type || "",
    }));
};

/** Origin of the manifest service (the API host root, not under /api). */
function manifestOrigin() {
  try {
    return new URL(getApiBaseUrl() || process.env.REACT_APP_BASE_URL || "").origin;
  } catch {
    return typeof window !== "undefined" ? window.location.origin : "";
  }
}

/**
 * The URL the preview plays and "Copy" hands out: { url, login_required, expires_at }.
 * When the tenant requires login it carries a signed token that expires.
 */
export const getPlaybackUrl = async (channelId) => {
  const data = unwrap(await get(`/playo/channels/${channelId}/playback`));
  return { url: `${manifestOrigin()}${data.path}`, login_required: !!data.login_required, expires_at: data.expires_at || null };
};
